import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bell } from 'lucide-react'
import logo from '../../assets/decida_logo_dark.png'
import Button from '../ui/Button.jsx'

const NAV_LINKS = [
  { label: 'How It Works', href: 'how-it-works' },
  { label: 'For Merchants', href: 'for-merchants' },
  { label: 'About', href: 'about' },
]

// All sections to observe (including ones not in the nav link list)
const SECTION_IDS = ['top', 'how-it-works', 'for-merchants', 'join', 'download', 'waitlist', 'about']

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeId, setActiveId] = useState('top')
  const location = useLocation()
  const isHome = location.pathname === '/'

  /* ── Shadow on scroll ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* ── Active section: highlight whichever section is in the upper half of viewport ── */
  useEffect(() => {
    const frameRef = { current: null }

    const update = () => {
      // Use 40% down the viewport as the trigger line — feels natural while reading
      const triggerY = window.scrollY + window.innerHeight * 0.4

      // Walk all sections; the last one whose top is above the trigger line is "active"
      let found = SECTION_IDS[0]
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top + window.scrollY <= triggerY) {
          found = id
        }
      }
      setActiveId(found)
    }

    const onScroll = () => {
      if (frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        update()
        frameRef.current = null
      })
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleLink = (href) => {
    setMenuOpen(false)
    scrollTo(href)
  }

  const getActive = (href) => {
    // join, download, and waitlist are between sections — highlight nothing while user is there
    if (activeId === 'join' || activeId === 'download' || activeId === 'waitlist') return false
    if (href === 'how-it-works') return activeId === 'how-it-works'
    if (href === 'for-merchants') return activeId === 'for-merchants'
    if (href === 'about') return activeId === 'about'
    return false
  }

  return (
    <header
      className={`nav-enter fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-white
        ${scrolled ? 'shadow-[0_1px_0_0_rgba(0,0,0,0.08)]' : ''}`}
    >
      <div className="section-container h-16 md:h-20 flex items-center justify-between">

        {/* ── Logo ─────────────────────────────────────── */}
        {isHome ? (
          <button
            onClick={() => scrollTo('top')}
            aria-label="Go to top"
            className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-primary rounded-md"
          >
            <img src={logo} alt="DecidA" className="w-28 md:w-36 h-auto" />
          </button>
        ) : (
          <Link
            to="/"
            aria-label="Go to home"
            className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-primary rounded-md"
          >
            <img src={logo} alt="DecidA" className="w-28 md:w-36 h-auto" />
          </Link>
        )}

        {/* ── Desktop nav ───────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map(({ label, href }) => {
            const isActive = getActive(href)
            return (
              <button
                key={href}
                onClick={() => handleLink(href)}
                aria-current={isActive ? 'page' : undefined}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium
                            transition-colors duration-200
                            ${isActive
                    ? 'text-primary bg-primary-light'
                    : 'text-brand-dark hover:text-primary hover:bg-primary-light'
                  }`}
              >
                {label}
                {/* Active underline indicator */}
                {isActive && (
                  <span
                    className="absolute bottom-0.5 left-1/2 -translate-x-1/2
                               h-0.5 w-4 rounded-full bg-primary
                               transition-all duration-300"
                  />
                )}
              </button>
            )
          })}
          {/* <Button
            variant="primary"
            className="ml-3 !py-2 !px-5 !text-sm"
            onClick={() => handleLink('join')}
          >
            Join as Merchant
          </Button> */}

          <Button
            variant="primary"
            className="btn-shimmer w-full sm:w-auto !px-8 !py-4 !text-[15px] !min-h-[52px]
                           hover:scale-[1.03] active:scale-[0.98]"
            onClick={() => scrollTo('waitlist')}
          >
            <Bell className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            Join the Waitlist
          </Button>
        </nav>

        {/* ── Mobile burger ─────────────────────────────── */}
        <button
          className="md:hidden p-2 rounded-lg text-brand-dark
                     hover:bg-gray-100 transition-colors"
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="sr-only">Menu</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2"
            viewBox="0 0 24 24" aria-hidden="true">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </div>

      {/* ── Mobile drawer ─────────────────────────────────── */}
      <div
        className={`md:hidden bg-white border-t border-gray-100
                    overflow-hidden transition-all duration-300
                    ${menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <nav className="section-container py-4 flex flex-col gap-1">
          {NAV_LINKS.map(({ label, href }) => {
            const isActive = getActive(href)
            return (
              <button
                key={href}
                onClick={() => handleLink(href)}
                aria-current={isActive ? 'page' : undefined}
                className={`text-left px-4 py-3 rounded-lg text-sm font-medium
                            transition-colors duration-200 flex items-center gap-2
                            ${isActive
                    ? 'text-primary bg-primary-light font-semibold'
                    : 'text-brand-dark hover:text-primary hover:bg-primary-light'
                  }`}
              >
                {isActive && (
                  <span className="w-1 h-4 rounded-full bg-primary flex-shrink-0" />
                )}
                {label}
              </button>
            )
          })}
          <Button
            variant="primary"
            className="mt-2 w-full"
            onClick={() => handleLink('join')}
          >
            Join as Merchant
          </Button>
        </nav>
      </div>
    </header>
  )
}
